<script setup lang="ts">
import { computed, ref } from 'vue';
import { Button } from '@toruslabs/vue-components/Button';
import { useAuthStore } from '../../store/authStore';
import { useClaimStore } from '../../store/claimStore';

// Assets
import IconBack from '../../assets/claim/icon-back.svg';
import TickGreen from '../../assets/claim/tick-green.svg';

const authStore = useAuthStore();
const claimStore = useClaimStore();

const mocaIdName = computed(() => {
    if (!claimStore.user.realmId) return '';
    const mocaIdName = claimStore.user.realmId.split('.')[0];
    validateFirstCondition(mocaIdName);
    validateSecondCondition(mocaIdName);
    return mocaIdName;
});
const mocaIdInput = ref("");
const isLoading = computed(() => claimStore.isLoading);
const firstCondition = ref(false);
const secondCondition = ref(false);

const onMocaInput = (event: Event) => {
    const inputValue = (event.target as HTMLInputElement).value;
    mocaIdInput.value = inputValue;
    validateFirstCondition(mocaIdInput.value);
    validateSecondCondition(mocaIdInput.value);
}

const validateFirstCondition = (mocaID: string) => {
    const regex = /^[a-zA-Z0-9]+$/;
    firstCondition.value = regex.test(mocaID);
}

const validateSecondCondition = (mocaID: string) => {
    secondCondition.value = mocaID.length >= 5 && mocaID.length <= 20;
}

const onClaimID = async () => {
    if (!firstCondition.value || !secondCondition.value) return;
    let name = mocaIdInput.value;
    if (mocaIdName.value) name = mocaIdName.value;
    claimStore.isLoading = true;
    await claimStore.reserveName(name);
    claimStore.isLoading = false;
}

async function logout() {
    if (isLoading.value) return;
    authStore.logout();
    claimStore.reset();
}
</script>

<template>
    <Button class="mb-2 !text-moca-gray-3 !text-sm !font-semibold !bg-moca-transparent !px-0" @on-click="logout">
        <img :src="IconBack" class="w-4" />
        <p>Back</p>
    </Button>
    <div class="flex flex-col items-center justify-center gap-4">
        <div class="flex flex-col items-start justify-center gap-2">
            <p class="text-white text-xl font-bold">Choose a name as your Moca ID</p>
            <p class="text-moca-gray-1 text-sm font-normal">
                This will be your name in Mocaverse, and
                <span class="text-white font-bold">cannot be changed</span>
                later on.
            </p>
        </div>
        <div v-if="mocaIdName" class="w-full">
            <p class="text-white text-lg font-semibold text-center">{{ mocaIdName }}.moca</p>
        </div>
        <div v-if="!mocaIdName" class="w-full rounded-full border-2 border-black h-12 flex bg-white items-center px-6">
            <input class="w-full focus:outline-none" placeholder="Enter your preferred name" :value="mocaIdInput"
                @input="event => onMocaInput(event)" />
            <p class="text-black text-base font-bold">.moca</p>
        </div>
        <div class="flex flex-col items-start justify-center gap-2 w-full mb-3">
            <p class="text-sm text-moca-white-80 font-normal">A valid Moca ID must:</p>
            <div class="flex flex-row gap-1 items-center">
                <div v-if="!firstCondition" class="bg-moca-yellow w-2 h-2 border border-black rounded-full"></div>
                <img v-if="firstCondition" :src="TickGreen" class="w-4" />
                <p class="text-sm" :class="{ 'text-moca-green': firstCondition, 'text-white': !firstCondition, }">
                    Include only letters or numbers
                </p>
            </div>
            <div class="flex flex-row gap-1 items-center">
                <div v-if="!secondCondition" class="bg-moca-yellow w-2 h-2 border border-black rounded-full"></div>
                <img v-if="secondCondition" :src="TickGreen" class="w-4" />
                <p class="text-sm" :class="{ 'text-moca-green': secondCondition, 'text-white': !secondCondition, }">
                    Character limit ranging from 5 to 20
                </p>
            </div>
        </div>
        <Button
            class="!bg-moca-yellow !text-black !text-lg !font-bold !border !border-black !h-14 !rounded-full !px-6 min-w-[200px]"
            :class="{ 'opacity-50': !firstCondition || !secondCondition || isLoading, }" @on-click="onClaimID"
            :loading="isLoading">
            Create Moca ID
        </Button>
        <p class="text-moca-gray-1 text-sm font-medium">
            *For Metamask user, you will need to sign a User Ops Message in Metamask.
        </p>
    </div>
</template>
